/**
 * 登录会话（`refresh_session`）的**纯逻辑**：设备归并、保留策略、批量移除目标。
 *
 * 为什么这些逻辑放在 shared 而不是写在路由里：
 *   1. 三条规则都需要**能被单测逐一钉住**（归并判据、保留策略、"批量移除绝不含当前会话"），
 *      而路由层要连库、连 Redis 才能跑起来；
 *   2. 归并判据必须**前后端同源** —— 服务端按它算 `deviceKey`，
 *      浏览器按它把会话归并成"设备"行。两边各写一份就会立刻不一致。
 *
 * 本文件不允许依赖数据库/HTTP（shared 的既有约定，见 index.ts）。
 *
 * ---------------------------------------------------------------- 背景
 *
 * 「下次不用输密码」每次登录都会新建一条 `refresh_session`；无痕模式每次丢掉
 * Cookie，于是旧记录**永远不会被再次使用**，却一直留在表里。生产库里
 * Apple 一个账号就攒了 107 条（其中 106 条已吊销），界面上又按"一条会话一行"
 * 逐条并列 —— 用户看到的是"一百多台登录设备"，而实际上那都是同一台电脑。
 */

/** 一条会话里本模块需要看到的字段（够用就好，避免把整行都拖进来）。 */
export interface SessionLike {
  id: string;
  /** 展示用标签（"Windows · Edge"）；缺失时由 `deviceLabelOf` 现算。 */
  deviceLabel?: string | null;
  /** 原始 User-Agent；有它才能算出稳定的设备指纹。 */
  userAgent?: string | null;
  ip?: string | null;
  /** 服务端已经算好的设备指纹；给了就直接用，避免两端算法漂移。 */
  deviceKey?: string | null;
  rememberDevice?: boolean;
  createdAt: string | Date;
  lastUsedAt: string | Date;
  expiresAt: string | Date;
  /** 非空 = 已吊销。 */
  revokedAt?: string | Date | null;
  /** 是否就是当前浏览器正在用的那条（服务端按 Refresh Cookie 判定）。 */
  current?: boolean;
}

// ---------------------------------------------------------------- 时间与系统/浏览器识别

/** 把 ISO 字符串或 Date 统一成毫秒数；无法解析时给 NaN（由调用方决定怎么处理）。 */
export function sessionTimeMs(value: string | Date | null | undefined): number {
  if (value === null || value === undefined) return Number.NaN;
  if (value instanceof Date) return value.getTime();
  return Date.parse(value);
}

export type DeviceOs = 'Windows' | 'macOS' | 'iPhone/iPad' | 'Android' | 'Linux' | '未知系统';
export type DeviceBrowser = 'Edge' | 'Chrome' | 'Safari' | 'Firefox' | '未知浏览器';

/**
 * 从 User-Agent（或已经生成的标签串）里识别系统与浏览器。
 *
 * 判断顺序是有讲究的，写错就会大面积误判：
 *  - **Edge 必须在 Chrome 之前**：Edge 的 UA 里同时含 `Edg/` 与 `Chrome/`；
 *  - **Firefox 必须在 Safari 之前**（Firefox 的 UA 里没有 `Safari`，但反过来写更稳）；
 *  - **iPhone/iPad 必须在 macOS 之前**：iPadOS 的桌面版 UA 里写着 `Macintosh; Intel Mac OS X`。
 *
 * 同时接受"原始 UA"和"已经拼好的标签"（如 `Windows · Edge`），
 * 因为历史行里可能只有 `device_label` 而没有 `user_agent`。
 */
export function parseUserAgent(value: string | null | undefined): {
  os: DeviceOs;
  browser: DeviceBrowser;
} {
  const s = (value ?? '').toLowerCase();

  const os: DeviceOs =
    s.includes('iphone') || s.includes('ipad') || s.includes('ipod')
      ? 'iPhone/iPad'
      : s.includes('android')
        ? 'Android'
        : s.includes('mac os') || s.includes('macos') || s.includes('darwin')
          ? 'macOS'
          : s.includes('windows')
            ? 'Windows'
            : s.includes('linux') || s.includes('x11')
              ? 'Linux'
              : '未知系统';

  const browser: DeviceBrowser = s.includes('edg')
    ? 'Edge'
    : s.includes('chrome') || s.includes('crios')
      ? 'Chrome'
      : s.includes('firefox') || s.includes('fxios')
        ? 'Firefox'
        : s.includes('safari')
          ? 'Safari'
          : '未知浏览器';

  return { os, browser };
}

/** 人类可读的设备标签。两边都认不出时给一个词，而不是拼成"未知系统 · 未知浏览器"。 */
export function deviceLabelOf(session: {
  userAgent?: string | null;
  deviceLabel?: string | null;
}): string {
  const { os, browser } = parseUserAgent(session.userAgent || session.deviceLabel || '');
  if (os === '未知系统' && browser === '未知浏览器') {
    // 现算不出来时，退回数据库里存着的标签（它至少是签发当时算的）
    return session.deviceLabel || '未知设备';
  }
  return `${os} · ${browser}`;
}

// ---------------------------------------------------------------- 归并判据

/** IP 为空时的占位。**不要**用空字符串：那会让"没记录 IP"与"IP 恰好为空"混在一起。 */
const UNKNOWN_IP = '未知IP';

/**
 * 设备指纹 = **系统 + 浏览器 + IP**。
 *
 * 为什么是这三样：用户看到的是"设备"，而账号设置里能拿到的、能区分设备的信息
 * 只有"什么系统、什么浏览器、从哪个 IP 来的"。同一个 IP 上不同浏览器（或不同系统）
 * 会被拆成两行，不同 IP 上的同一浏览器也会被拆成两行。
 *
 * 已知的**误并**边界（刻意保留，写在文档里而不是假装没有）：
 *   同一个局域网（同一个出口 IP）里两台**同样系统 + 同样浏览器**的电脑会被并成一行。
 *   缓解手段：界面显示"共 N 条会话"，且可以展开逐条移除。
 * 已知的**误拆**边界：
 *   手机换基站、路由器重新拨号 → IP 变了 → 会被看成新设备。
 *   对"清理登录记录"这个用途来说，误拆（多留一行）比误并（把别人的设备藏起来）安全。
 */
export function sessionDeviceKey(session: {
  deviceKey?: string | null;
  userAgent?: string | null;
  deviceLabel?: string | null;
  ip?: string | null;
}): string {
  // 服务端已经算好就直接用：这是"前后端同源"的落点，
  // 也保证浏览器端不需要拿到原始 UA 就能复现同一个指纹。
  if (typeof session.deviceKey === 'string' && session.deviceKey !== '') return session.deviceKey;

  const { os, browser } = parseUserAgent(session.userAgent || session.deviceLabel || '');
  const ip = (session.ip ?? '').trim();
  return `${os}|${browser}|${ip === '' ? UNKNOWN_IP : ip}`;
}

export interface SessionDeviceGroup<T extends SessionLike> {
  /** `sessionDeviceKey` 的结果；组内所有会话共用。 */
  key: string;
  /** 展示用标签（取组内最近使用的那条的标签）。 */
  label: string;
  ip: string | null;
  /** 组内会话，按最近使用倒序。 */
  sessions: T[];
  sessionCount: number;
  /** 组内最近一次使用时间（ISO 字符串或 Date，与传入值同型）。 */
  lastUsedAt: T['lastUsedAt'];
  /** 组内是否有"当前设备"的会话。 */
  hasCurrent: boolean;
  /** 组内是否有"免密（记住设备）"会话。 */
  rememberDevice: boolean;
}

/**
 * 把会话列表归并成"设备"列表：同一 `sessionDeviceKey` 的会话合成一组。
 *
 * 排序：设备按"最近使用"倒序（用户最关心的那台在最上面）；组内会话同样倒序。
 * 时间完全相同时用 key / id 兜底，保证同一份输入永远得到同一个顺序（测试才不会飘）。
 */
export function groupSessionsByDevice<T extends SessionLike>(sessions: T[]): SessionDeviceGroup<T>[] {
  const buckets = new Map<string, T[]>();
  for (const session of sessions) {
    const key = sessionDeviceKey(session);
    const bucket = buckets.get(key);
    if (bucket) bucket.push(session);
    else buckets.set(key, [session]);
  }

  const groups: SessionDeviceGroup<T>[] = [];
  for (const [key, bucket] of buckets) {
    const sorted = [...bucket].sort(
      (a, b) => sessionTimeMs(b.lastUsedAt) - sessionTimeMs(a.lastUsedAt) || a.id.localeCompare(b.id),
    );
    const newest = sorted[0]!;
    groups.push({
      key,
      label: deviceLabelOf(newest),
      ip: newest.ip ?? null,
      sessions: sorted,
      sessionCount: sorted.length,
      lastUsedAt: newest.lastUsedAt,
      hasCurrent: sorted.some((s) => s.current === true),
      rememberDevice: sorted.some((s) => s.rememberDevice === true),
    });
  }

  groups.sort(
    (a, b) =>
      sessionTimeMs(b.lastUsedAt) - sessionTimeMs(a.lastUsedAt) || a.key.localeCompare(b.key),
  );
  return groups;
}

// ---------------------------------------------------------------- 存活判定与保留策略

/** 会话是否"还活着"：没被吊销，且还没过期。 */
export function isSessionAlive(session: SessionLike, now: Date = new Date()): boolean {
  if (session.revokedAt !== null && session.revokedAt !== undefined) return false;
  const expires = sessionTimeMs(session.expiresAt);
  return Number.isFinite(expires) && expires > now.getTime();
}

/** 会话的"终止时刻"：被吊销的用吊销时间，否则用过期时间。 */
export function sessionTerminalMs(session: SessionLike): number {
  const revoked = sessionTimeMs(session.revokedAt);
  return Number.isFinite(revoked) ? revoked : sessionTimeMs(session.expiresAt);
}

/**
 * 保留策略（**唯一的定义处**，服务端、界面文案与文档都引用这里）：
 *
 *  - **永远保留**所有"还活着"的会话（未吊销且未过期）——
 *    清理绝不能把用户正在用的设备删掉；
 *  - 死会话（已吊销或已过期）里，**终止时刻在 `keepDeadDays` 天以内**的全部保留；
 *  - 死会话的**总数封顶 `keepDeadMax` 条**，超出时从**最旧的**开始删。
 *
 * 为什么按"终止时刻"而不是"最近使用时间"：无痕模式丢掉 Cookie 之后，
 * 那条会话**再也不会被使用**，`last_used_at` 会永远停在创建时刻 ——
 * 拿它当判据等于拿创建时刻当判据，说不清一条会话是"刚死"还是"死了很久"。
 *
 * 为什么除了时间窗还要有条数上限：
 *   令牌每次刷新都会轮换（旧行被标成已吊销、新行插入），所以**用得越勤，
 *   死行就越多**。只看"7 天内"的话，一个每天登录几十次的账号会在一周内
 *   攒下上百条死行 —— 那正是这次要清掉的东西（生产库：Apple 107 条，
 *   其中 106 条已吊销，且全都落在 7 天内）。加了 50 条上限后，
 *   表的大小只与"最近 50 次轮换"有关，不再随时间无限增长。
 *
 * 这项清理的**代价**（如实写在这里，不假装没有）：
 *   被删掉的已吊销会话原本还承担一个作用 —— 若有人拿**已经被轮换掉的旧令牌**
 *   来刷新，`auth.routes.ts` 会顺着 `previousTokenHash` 认出"令牌重放"并吊销该账号
 *   全部会话。删掉之后这个告警能力只覆盖仍然保留的那些行（7 天 / 最近 50 次轮换），
 *   更早的旧令牌被重放时只会被正常拒绝（401 SESSION_REVOKED），不会放行任何东西。
 *   换来的是一条**有界**的会话表，对一个家庭应用是合适的取舍。
 */
export const SESSION_RETENTION = {
  /** 死会话仍然保留的天数。 */
  keepDeadDays: 7,
  /** 死会话最多保留的条数；超出时从最旧的开始删。 */
  keepDeadMax: 50,
} as const;

export interface SessionRetentionLimits {
  now?: Date;
  keepDeadDays?: number;
  keepDeadMax?: number;
}

/**
 * 从一批会话里挑出**该删除**的那些（纯函数，返回原对象本身，调用方按 id 删）。
 *
 * 传入的可以包含活会话（会被原样放过，永不删除），因此调用方不需要先自己过滤。
 * 返回顺序 = 死会话按终止时刻倒序（最旧的排在最后），便于调用方观察"先删谁"。
 */
export function selectPurgeableSessions<T extends SessionLike>(
  sessions: T[],
  limits: SessionRetentionLimits = {},
): T[] {
  const now = limits.now ?? new Date();
  const keepDeadDays = limits.keepDeadDays ?? SESSION_RETENTION.keepDeadDays;
  const keepDeadMax = limits.keepDeadMax ?? SESSION_RETENTION.keepDeadMax;

  const dead: T[] = [];
  for (const session of sessions) {
    if (!isSessionAlive(session, now)) dead.push(session);
  }

  // 终止时刻倒序（终止时刻不可解析的排到最后）；同刻用 id 兜底，保证确定性。
  const ordered = [...dead].sort((a, b) => {
    const ta = sessionTerminalMs(a);
    const tb = sessionTerminalMs(b);
    const na = Number.isFinite(ta);
    const nb = Number.isFinite(tb);
    if (na !== nb) return na ? -1 : 1;
    if (na && nb && ta !== tb) return tb - ta;
    return a.id.localeCompare(b.id);
  });

  const cutoff = now.getTime() - keepDeadDays * 24 * 60 * 60 * 1000;
  return ordered.filter((session, index) => {
    // 超出条数上限：这条以及更旧的都删（ordered 是"新的在前"）
    if (index >= keepDeadMax) return true;
    const terminal = sessionTerminalMs(session);
    // 终止时刻算不出来的，当作"很久以前"处理（宁可删掉，免得永远留着）
    if (!Number.isFinite(terminal)) return true;
    return terminal <= cutoff;
  });
}

// ---------------------------------------------------------------- 批量移除

export interface RevokeOthersPlan<T extends SessionLike> {
  /** 是否**确认**了"当前会话"。false 时 `targets` 一定为空，调用方必须拒绝执行。 */
  identified: boolean;
  /** 当前会话的设备指纹；未确认时为 null。 */
  currentDeviceKey: string | null;
  /** 应当被吊销的会话（不含当前会话、也不含当前设备上的其它会话）。 */
  targets: T[];
}

/**
 * 规划"移除其它全部设备"：算出该吊销哪些会话。
 *
 * 安全底线（这是本函数存在的全部理由）：
 *   1. **当前会话永不进入 targets** —— 批量操作绝不能把用户自己踢下线；
 *   2. **当前设备上的其它会话也不进入 targets** ——
 *      同设备可能只是刚轮换出来的兄弟记录（一次刷新就会新旧交替），
 *      把它一起吊销，用户下次刷新时同样会被踢出去；
 *   3. **确认不了当前会话就什么都不做**（`identified: false`）——
 *      宁可让用户刷新页面重试，也不能退化成"全删"。调用方必须把这个
 *      当成失败来处理，而不是"那就都删了吧"。
 *
 * 只针对**还活着**的会话（已经吊销/过期的本来就不在列表里）。
 */
export function planRevokeOthers<T extends SessionLike>(
  sessions: T[],
  currentSessionId: string | null,
  now: Date = new Date(),
): RevokeOthersPlan<T> {
  if (!currentSessionId) return { identified: false, currentDeviceKey: null, targets: [] };

  const current = sessions.find((session) => session.id === currentSessionId);
  if (!current) return { identified: false, currentDeviceKey: null, targets: [] };

  const currentDeviceKey = sessionDeviceKey(current);
  const targets = sessions.filter(
    (session) =>
      session.id !== current.id &&
      sessionDeviceKey(session) !== currentDeviceKey &&
      isSessionAlive(session, now),
  );

  return { identified: true, currentDeviceKey, targets };
}
